import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import {
  format,
  startOfISOWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  startOfDay,
  endOfDay,
} from 'date-fns';
import { grey } from '@mui/material/colors';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { pillSelectSx } from './dashboardChartTokens';

export enum DatePeriods {
  ALL = 'All Records',
  TODAY = 'Today',
  THIS_WEEK = 'This Week',
  THIS_MONTH = 'This Month',
  THIS_QUARTER = 'This Quarter',
  YTD = 'Year To Date',
  CUSTOM = 'Custom',
}

const periodOptions = [
  { name: DatePeriods.ALL, value: '' },
  { name: DatePeriods.TODAY, value: format(new Date(), 'MMM dd, yyyy') },
  {
    name: DatePeriods.THIS_WEEK,
    value: `${format(startOfISOWeek(new Date()), 'MMM dd')} - ${format(
      new Date(),
      'MMM dd, yyyy'
    )}`,
  },
  {
    name: DatePeriods.THIS_MONTH,
    value: `${format(startOfMonth(new Date()), 'MMM dd')} - ${format(
      new Date(),
      'MMM dd, yyyy'
    )}`,
  },
  {
    name: DatePeriods.THIS_QUARTER,
    value: `${format(startOfQuarter(new Date()), 'MMM dd')} - ${format(
      new Date(),
      'MMM dd, yyyy'
    )}`,
  },
  {
    name: DatePeriods.YTD,
    value: `${format(startOfYear(new Date()), 'MMM dd')} - ${format(
      new Date(),
      'MMM dd, yyyy'
    )}`,
  },
  { name: DatePeriods.CUSTOM, value: 'Select Date Range' },
];

const startDateForPeriod = (period: DatePeriods): string | undefined => {
  switch (period) {
    case DatePeriods.TODAY:
      return startOfDay(new Date()).toISOString();
    case DatePeriods.THIS_WEEK:
      return startOfDay(startOfISOWeek(new Date())).toISOString();
    case DatePeriods.THIS_MONTH:
      return startOfDay(startOfMonth(new Date())).toISOString();
    case DatePeriods.THIS_QUARTER:
      return startOfDay(startOfQuarter(new Date())).toISOString();
    case DatePeriods.YTD:
      return startOfDay(startOfYear(new Date())).toISOString();
    default:
      return undefined;
  }
};

/** `Today` -> `today`, `This Week` -> `this week`, etc. */
const summaryFor = (
  period: DatePeriods,
  customStart?: string,
  customEnd?: string
): string => {
  switch (period) {
    case DatePeriods.TODAY:
      return 'today';
    case DatePeriods.THIS_WEEK:
      return 'this week';
    case DatePeriods.THIS_MONTH:
      return 'this month';
    case DatePeriods.THIS_QUARTER:
      return 'this quarter';
    case DatePeriods.YTD:
      return 'year to date';
    case DatePeriods.CUSTOM:
      return customStart && customEnd
        ? `from ${format(new Date(customStart), 'MMM d, yyyy')} to ${format(
            new Date(customEnd),
            'MMM d, yyyy'
          )}`
        : 'in the selected range';
    default:
      return 'all time';
  }
};

interface Props {
  /**
   * Fired once on mount and again whenever the selected range changes.
   * `startDate`/`endDate` are both undefined for "all time" — omit them from
   * the request rather than sending a sentinel. `summary` is a short
   * lowercase phrase (e.g. "this week", "all time") for the card subtitle.
   */
  onChange: (
    startDate: string | undefined,
    endDate: string | undefined,
    summary: string
  ) => void;
}

/**
 * Shared date-range control for the dashboard cards: a period dropdown
 * (All Records / Today / This Week / … / Custom) plus, for Custom, a from/to
 * date picker with an Apply button. One instance per card — `onChange` is how
 * the card learns the effective `startDate`/`endDate` to query with.
 */
const DateRangeFilter = ({ onChange }: Props) => {
  const [period, setPeriod] = useState<DatePeriods>(DatePeriods.ALL);
  const [customStartDate, setCustomStartDate] = useState<string>(
    new Date().toISOString()
  );
  const [customEndDate, setCustomEndDate] = useState<string>(
    new Date().toISOString()
  );

  // Report the initial "all time" range once on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => onChange(undefined, undefined, summaryFor(DatePeriods.ALL)), []);

  const handlePeriodChange = (event: SelectChangeEvent) => {
    const next = event.target.value as DatePeriods;
    setPeriod(next);

    if (next === DatePeriods.CUSTOM) return;

    const start = startDateForPeriod(next);
    onChange(start, start ? endOfDay(new Date()).toISOString() : undefined, summaryFor(next));
  };

  const applyCustomRange = () => {
    if (
      new Date(customStartDate).setHours(0, 0, 0, 0) >
      new Date(customEndDate).setHours(0, 0, 0, 0)
    ) {
      return;
    }
    const start = startOfDay(new Date(customStartDate)).toISOString();
    const end = endOfDay(new Date(customEndDate)).toISOString();
    onChange(start, end, summaryFor(DatePeriods.CUSTOM, start, end));
  };

  return (
    <>
      <FormControl>
        <Select
          value={period}
          onChange={handlePeriodChange}
          input={<OutlinedInput />}
          sx={pillSelectSx}
          renderValue={(value) => value as string}>
          {periodOptions.map((option) => (
            <MenuItem key={option.name} value={option.name}>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                <span style={{ fontWeight: 500, fontSize: 14 }}>
                  {option.name}
                </span>
                <span style={{ color: grey[600], fontSize: 12 }}>
                  {option.value}
                </span>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {period === DatePeriods.CUSTOM && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'flex-start',
              marginTop: 2,
            }}>
            <DatePicker
              data-cy={'select-date'}
              label="From"
              value={customStartDate}
              maxDate={new Date()}
              onChange={(newValue) => {
                setCustomStartDate(new Date(newValue).toISOString());
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  onKeyDown={(e) => e.preventDefault()}
                  error={
                    new Date(customStartDate).setHours(0, 0, 0, 0) >
                    new Date(customEndDate).setHours(0, 0, 0, 0)
                  }
                  helperText={
                    new Date(customStartDate).setHours(0, 0, 0, 0) >
                      new Date(customEndDate).setHours(0, 0, 0, 0) &&
                    `Date should be less than ${new Date(
                      customEndDate
                    ).toLocaleDateString()}`
                  }
                />
              )}
            />
            <DatePicker
              label="To"
              value={customEndDate}
              maxDate={new Date()}
              onChange={(newValue) => {
                setCustomEndDate(new Date(newValue).toISOString());
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  onKeyDown={(e) => e.preventDefault()}
                  error={
                    new Date(customStartDate).setHours(0, 0, 0, 0) >
                    new Date(customEndDate).setHours(0, 0, 0, 0)
                  }
                  helperText={
                    new Date(customStartDate).setHours(0, 0, 0, 0) >
                      new Date(customEndDate).setHours(0, 0, 0, 0) &&
                    `Date should be greater than ${new Date(
                      customStartDate
                    ).toLocaleDateString()}`
                  }
                />
              )}
            />
            <Button
              variant="contained"
              sx={{ minWidth: 120, marginTop: 0.25 }}
              onClick={applyCustomRange}>
              Apply
            </Button>
          </Box>
        </LocalizationProvider>
      )}
    </>
  );
};

export default DateRangeFilter;
